/**
 * ============================================================================
 * بناء caption موحَّد لرسائل واتساب — مصدر حقيقة واحد
 * ============================================================================
 *
 * المشكلة الجذريّة:
 *  - `shareImageWithLink()` تُلحق `link` آخر سطر داخل النصّ المنسوخ للحافظة.
 *  - أيّ caller يبني caption يحوي الرابط أصلاً ثمّ يمرّره → الرابط يظهر **مرّتين**.
 *
 * الحلّ:
 *  - كلّ مسارات مشاركة واتساب تستدعي `buildWhatsAppCaption(...)` فقط.
 *  - الـcaller يمرّر `includeLink: false` عند الذهاب إلى `shareImageWithLink`
 *    (لأنّها تُلحق الرابط بنفسها)، و`true` للنسخ المباشر / wa.me / النصّ الكامل.
 *
 * الدالّة pure: لا I/O، لا i18n، لا تنسيق — فقط تركيب الأجزاء الممرَّرة.
 * المتّصلون يبنون النصوص المُلَوّحة لغوياً ويمرّرونها كـ`body` و`giftLine`.
 * ============================================================================
 */

export type BuildWhatsAppCaptionInput = {
  /** النصّ الأساسيّ (مُلَوّح لغوياً من الـcaller). */
  body?: string | null;
  /** سطر هديّة اختياريّ (مُلَوّح لغوياً من الـcaller). */
  giftLine?: string | null;
  /** رابط السفير الكامل (لا يُلحَق إلّا حين includeLink=true). */
  link: string;
  /**
   * `true`  → يُضاف الرابط آخر سطر (للنسخ، wa.me، fallback نصّ كامل).
   * `false` → لا يُضاف الرابط (لأنّ `shareImageWithLink` ستُلحقه بنفسها).
   */
  includeLink: boolean;
};

/**
 * يجمع `body` + `giftLine` (+ `link` لو `includeLink`) بفاصل سطرين فارغين.
 * كلّ جزء فارغ يُتجاهَل تماماً (لا أسطر فارغة زائدة).
 *
 * @returns النصّ النهائيّ الجاهز للإرسال.
 */
export function buildWhatsAppCaption({
  body,
  giftLine,
  link,
  includeLink,
}: BuildWhatsAppCaptionInput): string {
  // Remove only this exact link before appending it. A different query/hash can
  // identify another product, campaign or section and must remain untouched.
  const b = link
    ? stripMatchingShareLinks(body?.trim() ?? "", link).trim()
    : body?.trim();
  const g = link
    ? stripMatchingShareLinks(giftLine?.trim() ?? "", link).trim()
    : giftLine?.trim();
  const parts: string[] = [];
  if (b) parts.push(b);
  if (g) parts.push(g);
  if (includeLink && link?.trim()) parts.push(link.trim());
  // درع طبقة ١: حتّى لو سرّب الـcaller الرابط حرفيّاً، نُزيل التكرار.
  return dedupeWhatsAppUrls(parts.join("\n\n"));
}

/**
 * Removes only the exact full target URL, including its query and fragment.
 * In particular, /a/CODE?to=one and /a/CODE?to=two are distinct destinations.
 *
 * إن فشل تحليل `targetLink` (ليس URL صالحاً) يُعاد النصّ كما هو دون لمس.
 */
export function stripMatchingShareLinks(text: string, targetLink: string): string {
  if (!text || !targetLink) return text;
  try {
    new URL(targetLink);
  } catch {
    return text;
  }
  const targetKey = targetLink.trim();
  const URL_RE = /https?:\/\/[^\s]+/g;
  let removed = 0;
  const cleaned = text.replace(URL_RE, (match) => {
    try {
      if (match === targetKey) {
        removed++;
        return "";
      }
    } catch {
      // ignore parse errors → اترك النصّ
    }
    return match;
  });
  if (removed > 0 && typeof console !== "undefined") {
    console.warn(
      `[whatsapp-strip-variants] أُزيل ${removed} نسخة من رابط المشاركة داخل body/giftLine لمنع التكرار`,
    );
  }
  return cleaned.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+\n/g, "\n");
}

/**
 * يُزيل أيّ URL مكرَّر حرفيّاً داخل نصّ واتساب — يُبقي **أوّل ظهور** فقط.
 *
 * يُطبَّق في ٣ طبقات دفاع:
 *   1) داخل `buildWhatsAppCaption` (مصدر الحقيقة).
 *   2) داخل `shareImageWithLink` على النصّ النهائيّ قبل المشاركة/الحافظة.
 *   3) داخل `buildShareUrl("whatsapp", ...)` قبل تكوين wa.me.
 *
 * المنطق pure: regex على `https?://...` بدون مكوّنات whitespace.
 * عند اكتشاف تكرار يُسجَّل `console.warn` بوسم `[whatsapp-dedupe]` ليُمسَك في
 * التطوير، لكنّ النصّ المُنظَّف يُعاد دائماً (لا exception كي لا تنكسر المشاركة
 * أمام المستخدم النهائيّ).
 */
export function dedupeWhatsAppUrls(text: string): string {
  if (!text) return text;
  const URL_RE = /https?:\/\/[^\s]+/g;
  const seen = new Set<string>();
  let removed = 0;
  const cleaned = text.replace(URL_RE, (match) => {
    if (seen.has(match)) {
      removed++;
      return ""; // أزل التكرار
    }
    seen.add(match);
    return match;
  });
  if (removed > 0 && typeof console !== "undefined") {
    console.warn(
      `[whatsapp-dedupe] أُزيل ${removed} رابط/روابط مكرَّرة من رسالة واتساب — راجع buildWhatsAppCaption call sites`,
    );
  }
  // نظّف أسطر فارغة متتالية ناتجة عن الإزالة
  return cleaned.replace(/\n{3,}/g, "\n\n").replace(/^\s+|\s+$/g, "").replace(/[ \t]+\n/g, "\n");
}

/**
 * نمط URL واسع يلتقط: http(s)://, www.x.y, دومين عاريّ (foo.com/bar),
 * wa.me/123، وروابط بدون بروتوكول. يُستخدم فقط لإدخال المستخدم في حقول
 * الهديّة (رسالة/أسماء) لمنع تسريب رابط ثانٍ إلى رسالة واتساب لاحقاً —
 * لأنّ النظام يُلحق رابط السفير تلقائياً.
 */
const USER_INPUT_URL_RE =
  /\b((?:https?:\/\/|www\.)[^\s]+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|co|ae|sa|me|app|link|store|shop|gift|lecmo)(?:\/[^\s]*)?)/gi;

/**
 * يُنظّف نصّاً جاء من إدخال المستخدم (رسالة هديّة، اسم، إلخ) بإزالة كلّ
 * ما يشبه رابطاً، ويُعيد النصّ النظيف + عدد ما أُزيل.
 *
 * هذا تحقّق وقت تشغيل دفاعيّ يضمن **مستحيليّة** ظهور رابطين في رسالة
 * واتساب لاحقاً مهما كان مصدر النصّ (إدخال يدويّ، لصق، قالب مناسبة).
 */
export function sanitizeUserInputForWhatsApp(text: string): {
  clean: string;
  removed: number;
} {
  if (!text) return { clean: "", removed: 0 };
  let removed = 0;
  const clean = text
    .replace(USER_INPUT_URL_RE, () => {
      removed++;
      return "";
    })
    // أزل مسافات متعدّدة ناتجة عن الإزالة
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +\n/g, "\n");
  if (removed > 0 && typeof console !== "undefined") {
    console.warn(
      `[whatsapp-user-input] أُزيل ${removed} رابط/روابط من إدخال المستخدم لمنع تكرار الرابط في رسالة واتساب`,
    );
  }
  return { clean, removed };
}
